<template>
  <div
    ref="viewportRef"
    :class="[
      ns.e('viewport'),
      { [ns.is('has-footer', props.showFooter)]: props.showFooter },
      { [ns.is('with-scroll-x', showSeparateXScroll)]: showSeparateXScroll },
    ]"
    :style="viewportStyle"
  >
    <div ref="headerScrollRef" :class="ns.e('header-viewport')">
      <grid-header-row fixed-type="" :overflow-x="overflowX" :columns="columns" />
    </div>

    <div
      :class="[
        ns.e('data-panel'),
        { [ns.is('with-scroll-x', showSeparateXScroll)]: showSeparateXScroll },
      ]"
    >
      <div
        ref="scrollRef"
        :class="[
          ns.e('body-wrapper'),
          { [ns.is('scroll-y', needsVerticalScroll)]: needsVerticalScroll },
          { [ns.is('fixed-x-scroll', showSeparateXScroll)]: showSeparateXScroll },
        ]"
        @scroll="onMainScroll"
      >
        <div :class="ns.e('body-content')">
          <div v-if="showEmptyState" :class="ns.e('empty')">
            {{ t('z.table.emptyText') }}
          </div>
          <grid-tree-virtual-rows
            v-else
            :columns="columns"
            :virtual-rows="virtualRows"
            :total-size="totalSize"
            :row-height="rowHeight"
            :table-width="tableWidth"
          />
        </div>
      </div>

      <div
        v-if="showSeparateXScroll"
        ref="scrollXRef"
        :class="ns.e('scroll-x')"
        @scroll="onScrollXBar"
      >
        <div
          :class="ns.e('scroll-x-inner')"
          :style="{ width: addUnit(scrollXContentWidth) }"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  computed,
  inject,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from 'vue'
import { useVirtualizer } from '@tanstack/vue-virtual'
import { useLocale, useNamespace } from '@zoryn/hooks'
import { addUnit } from '@zoryn/utils'
import { getGridRowHeight } from '@zoryn/components/grid/src/row-height'
import { useHorizontalScroll } from '@zoryn/components/grid/src/hooks/use-horizontal-scroll'
import { resolveTableWidth } from '@zoryn/components/grid/src/utils/fixed-column'
import { resolveScrollbarGutter } from '@zoryn/components/grid/src/utils/resolve-layout-width'
import {
  DEFAULT_EMPTY_BODY_HEIGHT,
  needsGridVerticalScroll,
  resolveBodyAreaHeight,
  resolveViewportHeight,
} from '@zoryn/components/grid/src/utils/resolve-viewport-height'
import GridHeaderRow from '@zoryn/components/grid/src/components/grid-header-row.vue'
import GridTreeVirtualRows from './grid-tree-virtual-rows.vue'
import type { CSSProperties } from 'vue'
import type { DataGridTree } from '../types'

const ns = useNamespace('grid')
const { t } = useLocale()
const $grid = inject('$grid') as DataGridTree
const { reactData, internalData, props } = $grid

const viewportRef = ref<HTMLElement | null>(null)
const headerScrollRef = ref<HTMLElement | null>(null)
const scrollRef = ref<HTMLElement | null>(null)
const scrollXRef = ref<HTMLElement | null>(null)
const overflowX = ref(false)
const verticalScrollbarWidth = ref(0)

const columns = computed(() => internalData.visibleColumns)
const tableData = computed(() => reactData.tableData)
const showEmptyState = computed(
  () =>
    !tableData.value.length &&
    !reactData.loading &&
    !reactData.loadError
)
const rowHeight = computed(() => getGridRowHeight($grid.gridSize.value))
const tableWidth = computed(() => resolveTableWidth(columns.value))
const showSeparateXScroll = computed(() => overflowX.value)

const needsVerticalScroll = computed(() =>
  needsGridVerticalScroll({
    rowCount: tableData.value.length,
    rowHeight: rowHeight.value,
    height: props.height,
    showFooter: props.showFooter,
    useFixedXScroll: showSeparateXScroll.value,
  })
)

const { showScrollHint } = useHorizontalScroll({
  wheelRef: viewportRef,
  scrollRef,
  hintRef: scrollXRef,
  hintFallbackRef: scrollRef,
  canScroll: overflowX,
  canScrollVertically: needsVerticalScroll,
})

let resizeObserver: ResizeObserver | undefined
let syncingScrollX = false

function syncHorizontalMirrors(left: number) {
  const headerEl = headerScrollRef.value
  if (headerEl && headerEl.scrollLeft !== left) {
    headerEl.scrollLeft = left
  }
}

function updateScrollbarPadding() {
  verticalScrollbarWidth.value = resolveScrollbarGutter(scrollRef.value)
}

function updateOverflowX() {
  const bodyEl = scrollRef.value
  const viewportEl = viewportRef.value
  if (!bodyEl || !viewportEl) {
    overflowX.value = false
    return
  }
  overflowX.value = bodyEl.scrollWidth > bodyEl.clientWidth + 1
}

function syncScrollXBarLeft(left: number) {
  const barEl = scrollXRef.value
  if (!barEl || syncingScrollX || barEl.scrollLeft === left) {
    return
  }
  syncingScrollX = true
  barEl.scrollLeft = left
  syncingScrollX = false
}

function onMainScroll(event: Event) {
  const target = event.target as HTMLElement
  syncScrollXBarLeft(target.scrollLeft)
  syncHorizontalMirrors(target.scrollLeft)
  $grid.closeContextMenu?.()
}

function onScrollXBar(event: Event) {
  const target = event.target as HTMLElement
  const bodyEl = scrollRef.value
  if (!bodyEl || syncingScrollX) {
    return
  }
  syncingScrollX = true
  bodyEl.scrollLeft = target.scrollLeft
  syncHorizontalMirrors(target.scrollLeft)
  syncingScrollX = false
}

onMounted(() => {
  $grid.bodyRef = viewportRef
  $grid.scrollToTop = async () => {
    if (scrollRef.value) {
      scrollRef.value.scrollTop = 0
    }
    await virtualizer.value.scrollToIndex(0, { align: 'start' })
  }
  updateOverflowX()
  updateScrollbarPadding()
  if (viewportRef.value) {
    resizeObserver = new ResizeObserver(() => {
      $grid.scheduleColumnWidthRecalc?.(scrollRef.value?.clientWidth)
      updateOverflowX()
      updateScrollbarPadding()
    })
    resizeObserver.observe(viewportRef.value)
  }
  if (scrollRef.value) {
    resizeObserver?.observe(scrollRef.value)
  }
  $grid.syncCurrentRowDom?.()
})

onBeforeUnmount(() => {
  resizeObserver?.disconnect()
})

const bodyAreaHeight = computed(() =>
  resolveBodyAreaHeight({
    rowHeight: rowHeight.value,
    rowCount: tableData.value.length,
    height: props.height,
    showFooter: props.showFooter,
    useFixedXScroll: showSeparateXScroll.value,
    fill:
      props.autoHeight === false
      && (props.height == null || props.height === ''),
  })
)

const viewportStyle = computed<CSSProperties>(() => {
  const style: CSSProperties = {
    '--z-grid-empty-body-height': `${DEFAULT_EMPTY_BODY_HEIGHT}px`,
    '--z-grid-body-height': `${bodyAreaHeight.value}px`,
    '--z-grid-scrollbar-width': `${verticalScrollbarWidth.value}px`,
  }

  if (props.height != null && props.height !== '') {
    style.height = addUnit(props.height)
    return style
  }

  if (
    props.autoHeight === false
    && (props.height == null || props.height === '')
  ) {
    return style
  }

  style.height = addUnit(
    resolveViewportHeight({
      rowHeight: rowHeight.value,
      rowCount: tableData.value.length,
      showFooter: props.showFooter,
      useFixedXScroll: showSeparateXScroll.value,
    })
  )
  return style
})

const scrollXContentWidth = computed(() => tableWidth.value)

const virtualizerOptions = computed(() => ({
  count: tableData.value.length,
  getScrollElement: () => scrollRef.value,
  estimateSize: () => rowHeight.value,
  overscan: 6,
}))

const virtualizer = useVirtualizer(virtualizerOptions)
const virtualRows = computed(() => virtualizer.value.getVirtualItems())
const totalSize = computed(() => virtualizer.value.getTotalSize())

watch(rowHeight, async () => {
  await nextTick()
  virtualizer.value.measure()
})

watch(
  () => $grid.gridSize.value,
  () => {
    $grid.scheduleColumnWidthRecalc?.(scrollRef.value?.clientWidth)
  }
)

watch(
  tableData,
  async () => {
    await nextTick()
    virtualizer.value.measure()
  },
  { flush: 'post' }
)

watch(needsVerticalScroll, async () => {
  await nextTick()
  virtualizer.value.measure()
  updateScrollbarPadding()
})

watch(tableData, () => {
  updateOverflowX()
  updateScrollbarPadding()
})
</script>
